import { AfterViewChecked, ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { QueryReportType, QueryReportTypeClass } from '@app/feature/dealer/query-reports/query-report-type.enum';
import { ReportType } from '@app/domain/model/report-type';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FormErrorHandler } from '@app/shared/form-error-handler';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { QueryReportsService } from '@app/feature/dealer/query-reports/query-reports.service';
import { validateQueryReportTimeframe } from '@app/shared/validators/query-report-timeframe.validator';
import { QueryReportsResultTransactionComponent } from '@app/feature/dealer/query-reports/result/transaction/query-reports-result-transaction.component';
import { DealerProfile } from '@app/domain/model/dealer-profile';
import { AuthenticationService } from '@app/shared/services/authentication.service';
import { TransactionReport } from './result/transaction/transaction-report';
import { SpeciesIndustryReport } from '@app/feature/dealer/query-reports/result/species-industry/species-industry-report';
import { QueryReportsResultSpeciesIndustryComponent } from '@app/feature/dealer/query-reports/result/species-industry/query-reports-result-species-industry.component';
import { MonthYear } from '@app/domain/model/monthYear';
import { UserAuthority } from '@app/domain/model/user-authority';
import { FormControlUtils } from '@app/util/form-group-utils';
import { GenerateIndustryReportService } from '@app/feature/dealer/query-reports/industry/generate-industry-report.service';
import { isDevMode } from '@angular/core';
import { appSettings } from '@app/shared/app-settings';

@Component({
  selector: 'app-dealer-query-reports-view',
  encapsulation: ViewEncapsulation.None,
  templateUrl: './dealer-query-reports-view.component.html',
  styleUrls: ['./dealer-query-reports-view.component.scss']
})
export class DealerQueryReportsViewComponent implements AfterViewChecked, OnInit {

  @Input()
  profile: DealerProfile;

  queryReportForm: FormGroup;

  queryReportTypes = QueryReportType;

  ReportType: typeof ReportType = ReportType;

  appSettings = appSettings;

  constructor(
    readonly formErrorHandler: FormErrorHandler,
    private readonly formBuilder: FormBuilder,
    private modalService: NgbModal,
    private readonly queryReportsService: QueryReportsService,
    private readonly generateIndustryReportsService: GenerateIndustryReportService,
    private authenticator: AuthenticationService,
    private cdRef: ChangeDetectorRef
  ) {
  }

  ngAfterViewChecked() {
    this.queryReportForm.markAsPristine();
    this.cdRef.detectChanges();
  }

  ngOnInit() {
    this.initForm();
  }

  onQueryFromSelect(item: MonthYear) {
    this.queryReportForm.markAsTouched();
    this.queryReportForm.get('queryFrom').markAsTouched();
  }

  onQueryToSelect(item) {
    this.queryReportForm.markAsTouched();
    this.queryReportForm.get('queryTo').markAsTouched();
  }


  onSubmit() {
    FormControlUtils.markAllTouched(this.queryReportForm);

    this.queryReportsService
      .queryReport(this.queryReportForm.value, this.getDealerId())
      .subscribe(
        (result: any) => {
          if (this.queryReportForm.value.queryReportType === QueryReportType.transactions.value) {
            this.showTransactionReport(TransactionReport.fromJSON(result));
          } else if (this.queryReportForm.value.queryReportType === QueryReportType.species.value) {
            this.showSpeciesReport(SpeciesIndustryReport.fromJSON(result));
          } else if (this.queryReportForm.value.queryReportType === QueryReportType.industry.value) {
            this.showIndustryReport(SpeciesIndustryReport.fromJSON(result));
          }
        },

        (error: Error) => {
          throw error;
        }
      );
  }

  private initForm() {
    this.queryReportForm = this.formBuilder.group({
        queryReportType: ['', Validators.required],
        queryFrom: [{year: null, month: null}, this.dateRequired],
        queryTo: [{year: null, month: null}, this.dateRequired ],
        reportType: ['', Validators.required]
      },
      { validator: validateQueryReportTimeframe }
    );
  }

  updateValidators(item) {
    if (this.isIndustry()) {
      this.queryReportForm.get('queryTo').setValidators(null);
      this.queryReportForm.get('queryTo').setValue({year: null, month: null});
      this.queryReportForm.get('queryTo').markAsUntouched();
      this.queryReportForm.get('reportType').setValidators(null);
      this.queryReportForm.get('reportType').setValue(null);
      this.queryReportForm.get('reportType').markAsUntouched();
    } else {
      this.queryReportForm.get('queryTo').setValidators(this.dateRequired);
      this.queryReportForm.get('reportType').setValidators(Validators.required);
    }
    this.queryReportForm.updateValueAndValidity();
  }


  private dateRequired(c: FormGroup) {
    if(!c.value.month || !c.value.year){
      return {
        dateRequired: {
          valid: false
        }
      };
    }
    return null;
  }


  isIndustry(): boolean {
    return this.queryReportForm.get('queryReportType').value === QueryReportType.industry.value;
  }

  private showReport(report: any, component: Object, queryReportType?: QueryReportTypeClass) {
    const modal = this.modalService.open(
      component,
      { backdrop: 'static', centered: true, keyboard: false, size: 'lg', windowClass: 'query-report' }
    );
    modal.componentInstance.profile = this.profile;
    modal.componentInstance.report = report;
    modal.componentInstance.queryReportType = queryReportType;
  }

  private showSpeciesReport(result: SpeciesIndustryReport) {
    this.showReport(result, QueryReportsResultSpeciesIndustryComponent, QueryReportType.species);
  }

  private showIndustryReport(result: SpeciesIndustryReport) {
    this.showReport(result, QueryReportsResultSpeciesIndustryComponent, QueryReportType.industry);
  }

  private showTransactionReport(result: TransactionReport) {
    this.showReport(result, QueryReportsResultTransactionComponent);
  }

  private getDealerId() {
    const executor = this.authenticator.getCurrentUser();
    return executor.hasAuthority(UserAuthority.DEALER_INFO) ? this.profile.id : null;
  }

  onGenerate() {
    this.generateIndustryReportsService
      .generateIndustryReport(this.queryReportForm.get('queryFrom').value)
      .subscribe(
        (result: string) => {
          window.alert(result);
        },

        (error: Error) => {
          window.alert('ERROR: '+ error.message);
        }
      );
    return null;
  }

  isDevMode() {
    return isDevMode();
  }
}
